import { Mention, MentionBlot } from "quill-mention";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import "quill-mention/dist/quill.mention.css";
import "./imageReference.css";

import { IImageMentionItem } from "../../@types/Note";
import { ToastContext } from "../../contexts/ToastContext";
import { sanitizeHTML } from "../../utils/htmlSanitizer";
import {
  createImageMentionConfig,
  registerImageReferenceBlot,
} from "./ImageReferenceMention";
import { mobileQuilllCss, quillCss } from "./RichTextEditor.styles";

export interface IMentionItem {
  id: string;
  value: string;
  [key: string]: string | undefined;
}

export interface IMentionConfig {
  source: (
    searchTerm: string,
    renderList: (matches: IMentionItem[], searchTerm: string) => void
  ) => void;
  renderItem?: (item: IMentionItem, searchTerm: string) => string | HTMLElement;
}

type RichTextEditorProps = {
  placeholderTxt?: string;
  height?: number;
  value?: string;
  readOnly?: boolean;
  hideToolbar?: boolean;
  onValueChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  mobile?: boolean;
  mentionConfig?: IMentionConfig;
  customImageHandler?: (file: File) => Promise<string>;
  disableImagePaste?: boolean;
  availableImages?: IImageMentionItem[];
  onImageReferenceInsert?: (imageKey: string, displayOrder: number) => void;
};

export function RichTextEditor({
  placeholderTxt,
  value,
  height = 300,
  hideToolbar = false,
  readOnly = false,
  onValueChange,
  mobile,
  onFocus,
  onBlur,
  mentionConfig,
  customImageHandler,
  disableImagePaste = false,
  availableImages = [],
  onImageReferenceInsert,
}: RichTextEditorProps) {
  const { t } = useTranslation();
  const { showToast } = useContext(ToastContext);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const isInitialized = useRef(false);

  // Use ref to store current availableImages so mention module can access latest value
  const availableImagesRef = useRef<IImageMentionItem[]>(availableImages);

  // Use ref to store Quill instance for mention insertion
  const quillInstanceRef = useRef<any>(null);

  // Update ref whenever availableImages prop changes
  useEffect(() => {
    availableImagesRef.current = availableImages;
  }, [availableImages]);

  const toolbar =
    readOnly || hideToolbar
      ? false
      : [["bold", "strike", "underline", "italic", "image", "link"]];
  const placeholder = readOnly
    ? ""
    : placeholderTxt ?? t("common_enterTextHere");

  // Build modules configuration with mention support.
  //
  // We can have two kinds of mentions in the editor:
  //   '@' → HCPs (tagging — handled by `mentionConfig` from the consumer)
  //   '#' → image references (image attachments inserted via dropdown)
  // quill-mention accepts a single module config; when both are active we merge
  // them into one config that dispatches based on the denotation char and the
  // shape of the item being rendered/selected.
  const modules: any = {
    toolbar,
  };

  const hcpMentionsEnabled = !!mentionConfig && !readOnly;
  const imageMentionsEnabled = !readOnly;

  if (hcpMentionsEnabled || imageMentionsEnabled) {
    const imageConfig: any = imageMentionsEnabled
      ? createImageMentionConfig(
          () => availableImagesRef.current,
          quillInstanceRef,
          onImageReferenceInsert
        )
      : null;

    const denotationChars: string[] = [];
    if (hcpMentionsEnabled) denotationChars.push("@");
    if (imageConfig) denotationChars.push("#");

    modules.mention = {
      mentionDenotationChars: denotationChars,
      allowedChars: /^[A-Za-z0-9._@#-]*$/,
      isolateCharacter: true,
      spaceAfterInsert: true,
      minChars: 0,
      showDenotationChar: true,
      // Anchor the dropdown to the caret position. `fixMentionsToQuill: true`
      // forced the menu to the editor container's bottom-left regardless of
      // where the user was typing — fine for the image flow that always pulled
      // from the bottom, wrong for inline @ mentions.
      defaultMenuOrientation: "bottom" as const,
      offsetTop: 2,
      offsetLeft: 0,
      // dataAttributes from the image config — HCP mentions only need the
      // defaults (id, value, denotationChar) which are always included.
      dataAttributes: imageConfig?.dataAttributes ?? [
        "id",
        "value",
        "denotationChar",
      ],
      listItemClass: "ql-mention-list-item",
      mentionContainerClass: "ql-mention-list-container",
      source: (
        searchTerm: string,
        renderList: (matches: any[], searchTerm: string) => void,
        mentionChar: string
      ) => {
        if (mentionChar === "@" && mentionConfig) {
          mentionConfig.source(searchTerm, renderList);
          return;
        }
        if (mentionChar === "#" && imageConfig) {
          imageConfig.source(searchTerm, renderList);
          return;
        }
        renderList([], searchTerm);
      },
      renderItem: (item: any, searchTerm: string) => {
        // Image items carry a `displayOrder`; HCP items don't.
        if (
          item &&
          typeof item === "object" &&
          "displayOrder" in item &&
          imageConfig?.renderItem
        ) {
          return imageConfig.renderItem(item, searchTerm);
        }
        if (mentionConfig?.renderItem) {
          return mentionConfig.renderItem(item, searchTerm);
        }
        return String(item?.value ?? "");
      },
      onSelect: (item: any, insertItem: (data: any) => void) => {
        // Image references replace the mention with a custom blot; fall back
        // to quill-mention's default insertion for HCP mentions.
        if (
          item &&
          typeof item === "object" &&
          "displayOrder" in item &&
          imageConfig?.onSelect
        ) {
          imageConfig.onSelect(item, insertItem);
          return;
        }
        insertItem(item);
      },
    };
  }

  const toolBarHeight = readOnly ? 0 : 42;

  const formats = [
    "bold",
    "strike",
    "underline",
    "italic",
    "image",
    "link",
    "mention",
  ];

  const { quill, quillRef, Quill } = useQuill({
    modules,
    placeholder,
    readOnly,
    formats,
  });

  useEffect(() => {
    if (quill && Quill) {
      // Store quill instance in ref for mention module access
      quillInstanceRef.current = quill;

      const Delta = Quill.import("delta");

      // Block base64 images and preserve src for blob/note-image URLs.
      quill.clipboard.addMatcher("IMG", (node: any, delta: any) => {
        const src = node.getAttribute("src");
        if (src && src.startsWith("data:image")) {
          return new Delta();
        }
        return delta;
      });

      // quill-mention registers the MentionBlot (Embed) but does NOT register
      // a clipboard matcher for span.mention. So when we paste saved HTML
      // back into the editor (Edit modal), Quill parses the mention as a
      // generic <span> + child spans instead of an atomic embed — the caret
      // can sit inside it and Backspace edits the inner text instead of
      // removing the tag. Register a matcher that converts span.mention back
      // into an embed delta so the blot is re-created as atomic on paste.
      quill.clipboard.addMatcher("SPAN.mention", (node: Node) => {
        const el = node as HTMLElement;
        const data: Record<string, string | undefined> = {
          id: el.dataset.id,
          value: el.dataset.value,
          denotationChar: el.dataset.denotationChar ?? "@",
        };
        if (el.dataset.index !== undefined) {
          data.index = el.dataset.index;
        }
        return new Delta().insert({ mention: data });
      });

      // Sanitize HTML output before passing to parent component
      quill.on("text-change", () => {
        const html = quill.root.innerHTML;
        const sanitizedHtml = sanitizeHTML(html);
        onValueChange(sanitizedHtml);
      });

      quill.on("selection-change", (range: any, oldRange: any) => {
        if (range === null && oldRange !== null) {
          if (onBlur) {
            onBlur();
          }
        } else if (range !== null && oldRange === null) {
          if (onFocus) {
            onFocus();
          }
        }
      });

      // Custom image handler for in-memory storage
      if (customImageHandler) {
        const toolbar = quill.getModule("toolbar") as any;
        toolbar.addHandler("image", () => {
          if (isUploadingImage) {
            return;
          }

          const input = document.createElement("input");
          input.setAttribute("type", "file");
          input.setAttribute(
            "accept",
            "image/png, image/jpeg, image/jpg, image/gif, image/webp, image/bmp"
          );
          input.click();

          input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;

            try {
              setIsUploadingImage(true);

              const imageUrl = await customImageHandler(file);
              const range = quill.getSelection(true);

              quill.insertEmbed(range.index, "image", imageUrl);
              quill.setSelection(range.index + 1, 0);
            } catch (error: any) {
              showToast(error.message || "Failed to process image", "error");
            } finally {
              setIsUploadingImage(false);
            }
          };
        });
      }

      // Handle image paste - route through customImageHandler if provided
      let cleanupPasteHandler: (() => void) | undefined;
      if (disableImagePaste && customImageHandler) {
        const editor = quill.root;

        const handlePaste = (event: Event) => {
          const clipboardEvent = event as ClipboardEvent;
          const items = clipboardEvent.clipboardData?.items;

          if (items) {
            let imageFile: File | null = null;
            for (let i = 0; i < items.length; i += 1) {
              if (items[i].type.indexOf("image") !== -1) {
                imageFile = items[i].getAsFile();
                break;
              }
            }

            if (imageFile) {
              event.preventDefault();
              event.stopPropagation();

              (async () => {
                try {
                  setIsUploadingImage(true);

                  const imageUrl = await customImageHandler(imageFile!);
                  const range = quill.getSelection(true) || {
                    index: 0,
                    length: 0,
                  };

                  quill.insertEmbed(range.index, "image", imageUrl);
                  quill.setSelection(range.index + 1, 0);
                } catch (error: any) {
                  showToast(
                    error.message || "Failed to process image",
                    "error"
                  );
                } finally {
                  setIsUploadingImage(false);
                }
              })();
            }
          }
        };

        editor.addEventListener("paste", handlePaste, true);

        cleanupPasteHandler = () => {
          editor.removeEventListener("paste", handlePaste, true);
        };
      } else if (disableImagePaste) {
        const editor = quill.root;

        const handlePaste = (event: Event) => {
          const clipboardEvent = event as ClipboardEvent;
          const items = clipboardEvent.clipboardData?.items;

          if (items) {
            for (let i = 0; i < items.length; i += 1) {
              if (items[i].type.indexOf("image") !== -1) {
                event.preventDefault();
                event.stopPropagation();
                showToast(
                  "Please use the image button from the toolbar to add images",
                  "warning"
                );
                return;
              }
            }
          }
        };

        editor.addEventListener("paste", handlePaste, true);

        cleanupPasteHandler = () => {
          editor.removeEventListener("paste", handlePaste, true);
        };
      }

      // SECURITY: Sanitize incoming HTML before setting it. This prevents
      // stored XSS attacks when displaying saved content. Only set the
      // initial value once to prevent duplication on re-renders, EXCEPT in
      // readOnly mode where we want to update when value changes.
      if (!isInitialized.current || readOnly) {
        if (value) {
          const sanitizedValue = sanitizeHTML(value);
          if (readOnly) {
            quill.setText("");
          }
          quill.clipboard.dangerouslyPasteHTML(0, sanitizedValue);
        } else {
          quill.setText("");
        }
        if (!readOnly) {
          isInitialized.current = true;
        }
      }

      return cleanupPasteHandler;
    }

    return undefined;
  }, [
    quill,
    Quill,
    customImageHandler,
    disableImagePaste,
    isUploadingImage,
    value,
    readOnly,
    onValueChange,
    onFocus,
    onBlur,
    showToast,
    availableImages,
    onImageReferenceInsert,
  ]);

  if (Quill && !quill) {
    // Add https to link if https is not present
    const Link = Quill.import("formats/link");
    Link.sanitize = function sanitize(url: string) {
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        return `http://${url}`;
      }
      return url;
    };
    Quill.register(Link, true);

    // Don't sanitize image src attributes - preserve blob URLs and note-image: refs
    const Image = Quill.import("formats/image");
    const originalSanitize = Image.sanitize;
    Image.sanitize = function sanitize(url: string) {
      if (url.startsWith("blob:") || url.startsWith("note-image:")) {
        return url;
      }
      return originalSanitize(url);
    };
    Quill.register(Image, true);

    // Register custom image reference blot for mentions
    registerImageReferenceBlot(Quill);

    // Register both the quill-mention module AND its inline blot. Registering
    // only the module (the dev pattern) makes `Editor.insertEmbed` fail with
    // "Unable to create mention blot" because Quill 2's Scroll registry has
    // no entry for `blots/mention`. quill-mention's autoregister entry point
    // registers both — we do the same here explicitly.
    Quill.register(
      { "blots/mention": MentionBlot, "modules/mention": Mention },
      true
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          height,
          fontSize: mobile ? "18px" : "14px",
        }}
        className={mobile ? mobileQuilllCss() : quillCss()}
      >
        <div
          style={{
            width: "calc(100% - 40)",
            height: height - toolBarHeight,
            backgroundColor: "rgba(233, 230, 244, 0.16)",
            borderRadius: "6px",
            marginTop: "20px",
            border: "1px solid #E6E6E6",
            color: "rgba(33, 33, 33, 0.50)",
          }}
          ref={quillRef}
        />
      </div>
    </div>
  );
}
